import {
  acceptInvitationResponseSchema,
  invitationListSchema,
  invitationPreviewSchema,
  invitationSchema,
  type AcceptInvitationRequest,
  type CreateInvitationRequest,
  type RevokeRequest,
} from '@mfos/shared';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiPath, apiPost } from '../../lib/api-client';
import { workspaceKey, workspacePath } from '../workspace/workspace.api';
import { useWorkspaceMutation } from '../workspace/workspace-mutation';

export const invitationsKey = (workspaceId: string) =>
  [...workspaceKey(workspaceId), 'invitations'] as const;

/** Preview is a read: POST only so the single-use token stays out of URLs and logs. */
export function useInvitationPreview(token: string | null) {
  return useQuery({
    queryKey: ['invitation-preview', token],
    queryFn: ({ signal }) =>
      apiPost(apiPath('invitations', 'preview'), { token }, invitationPreviewSchema, signal),
    enabled: token !== null,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export const acceptInvitation = (body: AcceptInvitationRequest) =>
  apiPost(apiPath('invitations', 'accept'), body, acceptInvitationResponseSchema);

export function useInvitations(workspaceId: string) {
  return useQuery({
    queryKey: invitationsKey(workspaceId),
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'invitations'), { schema: invitationListSchema, signal }),
  });
}

export function useCreateInvitation(workspaceId: string) {
  return useWorkspaceMutation(workspaceId, (body: CreateInvitationRequest) =>
    apiPost(workspacePath(workspaceId, 'invitations'), body, invitationSchema),
  );
}

export function useRevokeInvitation(workspaceId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({ invitationId, body }: { invitationId: string; body: RevokeRequest }) =>
      apiPost(
        workspacePath(workspaceId, 'invitations', invitationId, 'revoke'),
        body,
        invitationSchema,
      ),
  );
}

export function useResendInvitation(workspaceId: string) {
  return useWorkspaceMutation(workspaceId, (invitationId: string) =>
    apiPost(
      workspacePath(workspaceId, 'invitations', invitationId, 'resend'),
      undefined,
      invitationSchema,
    ),
  );
}
